import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import PixelCoffee from '../ui/PixelCoffee';

interface CryptoOption {
  name: string;
  symbol: string;
  address: string;
  color: string;
  bgColor: string;
  icon: React.ReactNode;
}

const cryptoOptions: CryptoOption[] = [
  {
    name: 'Bitcoin',
    symbol: 'BTC',
    address: 'bc1qgulxukk9nzy3zgdjtj0fpghrskfp94ek9h39yh',
    color: '#F7931A',
    bgColor: 'rgba(247, 147, 26, 0.1)',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
        <path d="M23.638 14.904c-1.602 6.43-8.113 10.34-14.542 8.736C2.67 22.05-1.244 15.525.362 9.105 1.962 2.67 8.475-1.243 14.9.358c6.43 1.605 10.342 8.115 8.738 14.546zm-6.35-4.613c.24-1.59-.974-2.45-2.64-3.03l.54-2.153-1.315-.33-.52 2.107c-.345-.087-.705-.167-1.064-.25l.526-2.127-1.32-.33-.54 2.165c-.285-.067-.565-.132-.84-.2l-1.815-.45-.35 1.407s.975.225.955.236c.535.136.63.486.615.766l-1.477 5.92c-.075.166-.24.406-.614.314.015.02-.96-.24-.96-.24l-.66 1.51 1.71.426.93.242-.54 2.19 1.32.327.54-2.17c.36.1.705.19 1.05.273l-.51 2.154 1.32.33.545-2.19c2.24.427 3.93.257 4.64-1.774.57-1.637-.03-2.58-1.217-3.196.854-.193 1.5-.76 1.68-1.93h.01zm-3.01 4.22c-.404 1.64-3.157.75-4.05.53l.72-2.9c.896.23 3.757.67 3.33 2.37zm.41-4.24c-.37 1.49-2.662.735-3.405.55l.654-2.64c.744.18 3.137.52 2.75 2.084v.006z" />
      </svg>
    ),
  },
  {
    name: 'Ethereum',
    symbol: 'ETH',
    address: '0xcBD51913077341EbEAe2cB25d03E4a79684fC0c2',
    color: '#627EEA',
    bgColor: 'rgba(98, 126, 234, 0.1)',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
        <path d="M11.944 17.97L4.58 13.62 11.943 24l7.37-10.38-7.372 4.35h.003zM12.056 0L4.69 12.223l7.365 4.354 7.365-4.35L12.056 0z" />
      </svg>
    ),
  },
  {
    name: 'Solana',
    symbol: 'SOL',
    address: '3w9Qbsq9PKTexBLCfhwU7UZ9agJN2kzoejuGXuJX6wEW',
    color: '#9945FF',
    bgColor: 'rgba(153, 69, 255, 0.1)',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
        <path d="M4.136 5.212a.694.694 0 01.492-.204h18.212a.347.347 0 01.246.593l-3.472 3.471a.694.694 0 01-.492.204H.91a.347.347 0 01-.246-.593l3.472-3.471zm0 13.576a.694.694 0 01.492-.204h18.212a.347.347 0 01.246.593l-3.472 3.471a.694.694 0 01-.492.204H.91a.347.347 0 01-.246-.593l3.472-3.471zm18.704-5.035a.694.694 0 00-.492-.204H4.136a.347.347 0 00-.246.593l3.472 3.471a.694.694 0 00.492.204h18.212a.347.347 0 00.246-.593l-3.472-3.471z" />
      </svg>
    ),
  },
  {
    name: 'XRP',
    symbol: 'XRP',
    address: 'rQLPT9JvTUAe8vQoJJ5NpkU8V1H5piGAEa',
    color: '#FFFFFF',
    bgColor: 'rgba(255, 255, 255, 0.1)',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
        <path d="M1.503 0h3.942l5.865 5.865a3.793 3.793 0 005.38 0L22.559 0h3.942l-8.28 8.28a6.706 6.706 0 01-9.488 0L.454 0h1.05zm21.053 24h-3.942l-5.923-5.923a3.793 3.793 0 00-5.38 0L1.387 24H-2.5l8.28-8.28a6.706 6.706 0 019.488 0l8.28 8.28h-1.05.058z" />
      </svg>
    ),
  },
];

const Support = () => {
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);
  const [expandedCard, setExpandedCard] = useState<string | null>(null);

  const copyToClipboard = async (address: string, symbol: string) => {
    try {
      await navigator.clipboard.writeText(address);
      setCopiedAddress(symbol);
      setTimeout(() => setCopiedAddress(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const toggleCard = (symbol: string) => {
    setExpandedCard(expandedCard === symbol ? null : symbol);
  };

  return (
    <section id="support" className="relative section-padding bg-bg-darker overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-bg-dark via-bg-darker to-bg-dark pointer-events-none" />

      <div className="container-custom relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-4 mb-4">
              <PixelCoffee size="lg" className="animate-bounce-slow" />
              <h2 className="text-4xl md:text-5xl font-heading font-bold">
                Buy Me a <span className="gradient-text">Coffee</span>
              </h2>
              <PixelCoffee size="lg" className="animate-bounce-slow-delayed" />
            </div>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto mb-2">
              Your contributions help fund my education and future research projects.
              Thank you for your support!
            </p>
            <p className="text-sm text-text-secondary/70">
              Tap a currency to reveal the QR code. If your wallet requires a tag or memo, add it as needed.
            </p>
          </div>

          {/* Crypto Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {cryptoOptions.map((crypto) => (
              <div
                key={crypto.symbol}
                className="glass rounded-lg overflow-hidden transition-all duration-300 hover:border-glass-border/50"
              >
                {/* Card Header - Always visible */}
                <button
                  onClick={() => toggleCard(crypto.symbol)}
                  className="w-full p-3 flex flex-col items-center text-center transition-colors hover:bg-white/5"
                >
                  <div
                    className="p-2 rounded-lg mb-2"
                    style={{ backgroundColor: crypto.bgColor, color: crypto.color }}
                  >
                    <div className="w-6 h-6">
                      {crypto.icon}
                    </div>
                  </div>
                  <h3 className="text-sm font-heading font-semibold">{crypto.symbol}</h3>
                </button>

                {/* Expandable Content */}
                <div
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    expandedCard === crypto.symbol ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-3 pb-3 space-y-3">
                    {/* QR Code */}
                    <div className="flex justify-center">
                      <div className="bg-white p-2 rounded-lg">
                        <QRCodeSVG
                          value={crypto.address}
                          size={100}
                          level="M"
                          includeMargin={false}
                        />
                      </div>
                    </div>

                    {/* Address + Copy */}
                    <div className="flex flex-col gap-2">
                      <code className="text-[10px] bg-bg-dark p-2 rounded-lg break-all text-text-secondary font-mono leading-tight">
                        {crypto.address}
                      </code>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(crypto.address, crypto.symbol);
                        }}
                        className="w-full py-2 bg-bg-dark hover:bg-quantum-blue/20 rounded-lg transition-colors text-xs flex items-center justify-center gap-1"
                        title="Copy address"
                      >
                        {copiedAddress === crypto.symbol ? (
                          <>
                            <svg className="w-4 h-4 text-quantum-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="text-quantum-green">Copied!</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            <span className="text-text-secondary">Copy</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Thank you note with contact form CTA */}
          <div className="mt-10 glass rounded-xl p-6 text-center">
            <p className="text-text-secondary mb-3">
              After sending a contribution, please use the{' '}
              <a href="#contact" className="text-quantum-blue hover:text-quantum-purple transition-colors underline">
                contact form
              </a>
              {' '}to share your transaction ID and a note. I'd love to reach out and say thank you personally!
            </p>
            <p className="text-text-secondary/60 text-sm">
              Always double-check the address before sending. Transactions are irreversible.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Support;
